import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  RouterModule,
  ActivatedRoute,
  Router,
} from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import {
  ModalComponent,
  ModalType,
} from '../../../shared/components/ui/modal/modal.component';
import { DocumentViewerModalComponent } from '../../../shared/components/ui/document-viewer-modal/document-viewer-modal.component';
import { AuditModalComponent } from '../../../shared/components/ui/audit-modal/audit-modal.component';
import { UserProfile, UserRole } from '../../../shared/models/auth.model';
import { AuthService } from '../../../core/services/auth.service';
import {
  RoutingSlipDetails,
  DocumentAttachment,
  Comment,
} from '../../../shared/models/routing-slip.model';
import { DocusignModule } from '../../../shared/models/docusign.model';
import { RoutingSlipService } from '../../../shared/services/routing-slip.service';
import { DocusignService } from '../../../shared/services/docusign.service';
import { DocumentService } from '../../../shared/services/document.service';
import { UsersService } from '../../../shared/services/users.service';
import { AuditsData } from '../../../shared/models';
import { MessageService } from 'primeng/api';
import {
  isEarlierRoutingLevelPending,
  WAIT_FOR_PREVIOUS_USER_MESSAGE,
} from '../../../shared/utils/routing-slip-turn.util';
import { Observable, of, forkJoin, throwError } from 'rxjs';
import { map, tap, catchError, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-routing-slip-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    ToastModule,
    TooltipModule,
    ModalComponent,
    DocumentViewerModalComponent,
    AuditModalComponent,
  ],
  providers: [MessageService],
  templateUrl: './routing-slip-details.component.html',
  styleUrl: './routing-slip-details.component.scss',
})
export class RoutingSlipDetailsComponent implements OnInit, OnDestroy {
  private readonly docuSignPendingStorageKey = 'routingSlipDocuSignPending';
  private readonly docuSignEnvelopeIdStorageKey = 'routingSlipDocuSignEnvelopeId';
  /** Comment for `POST .../approvereject` after DocuSign (from Approve or Reject modal). */
  private readonly docuSignPendingCommentStorageKey = 'routingSlipDocuSignApprovalComment';
  /** `'approve' | 'reject'` — which modal started the DocuSign flow (legacy: missing ⇒ treat as approve). */
  private readonly docuSignPendingActionStorageKey = 'routingSlipDocuSignPendingAction';
  /** Return tab signals success to the opener via `localStorage` when `window.opener` was cleared (cross-origin). */
  private readonly docuSignApprovalSavedBroadcastKey =
    'routingSlipDocuSignApprovalSavedBroadcast';
  /**
   * After `POST .../approvereject` in the return tab, the opener may be navigated via
   * `location.replace(returnHref)` (full reload). This one-shot lets the parent show the same success toast.
   */
  private readonly docuSignSuccessToastStorageKey =
    'routingSlipDocuSignSuccessToast';
  private docuSignWindowPollId: ReturnType<typeof setInterval> | null = null;
  /** `postMessage` from the DocuSign return tab; removed in ngOnDestroy. */
  private docuSignOpenerMessageHandler:
    | ((ev: MessageEvent) => void)
    | null = null;
  /** `storage` (other documents only) when return tab cannot use `window.opener`. */
  private docuSignStorageHandler: ((ev: StorageEvent) => void) | null = null;
  private lastDocusignApprovalSavedHandledAt = 0;
  /** Prevents double toasts if both postMessage and a duplicate event fire. */
  private lastDocuSignOpenerNotifyKey = '';
  private lastDocuSignOpenerNotifyTime = 0;

  /** True after opening DocuSign in a new tab until success, cancel, or tab-close handling. */
  isDocuSignSigningInProgress = false;
  /**
   * True from Sign and Submit (DocuSign) on Approve/Reject modal until DocuSign tab opens or fails.
   */
  isDocuSignApproveSubmitting = false;

  // Modal state
  showModal: boolean = false;
  modalType: ModalType = 'comment';
  modalTitle: string = '';
  /** Label for comment / approve / reject modal primary action */
  modalSubmitButtonLabel: string = 'Submit';

  // Archive modal state
  showDeleteModal: boolean = false;
  isDeleting: boolean = false;

  // Role-based access control
  isManager: boolean = false;
  isSecretary: boolean = false;
  isAdmin: boolean = false;

  // Mobile detection
  isMobile = window.innerWidth < 640;

  // Document viewer modal state
  showDocumentViewer: boolean = false;
  currentDocument: DocumentAttachment | null = null;

  // User profile data - will be set dynamically from authenticated user
  userProfile: UserProfile | null = null;

  // Routing slip details
  routingSlipDetails: RoutingSlipDetails | null = null;
  loadErrorTitle = 'Routing slip not found';
  loadErrorMessage = 'This routing slip is not available.';
  loadErrorIcon = 'pi-file';
  loading: boolean = false;
  documentName: string = '';

  // UI data for redesigned layout - populated from API
  assignedUsers: Array<{
    name: string;
    status: 'Approved' | 'Rejected' | 'Pending' | 'Viewed';
    date: string;
    time: string;
    comment?: string;
  }> = [];

  assignedUserLevels: Array<{
    level: number;
    permissionLabel: string;
    permissionValues: number[];
    users: Array<{
      name: string;
      status: 'Approved' | 'Rejected' | 'Pending' | 'Viewed';
      date: string;
      time: string;
      comment?: string;
    }>;
  }> = [];

  comments: Array<{ text: string; by: string; date: string; time: string }> =
    [];

  // User details cache
  userDetailsCache: Map<string, { name: string; email: string }> = new Map();

  // Permission-based button visibility
  userPermission: number | null = null;
  showCommentButton: boolean = false;
  showApproveButton: boolean = false;
  showRejectButton: boolean = false;
  showMarkAsViewedButton: boolean = false;
  showAuditButton: boolean = true; // Always show audit button
  showArchiveButton: boolean = true; // Always show archive button
  isMarkedAsViewed: boolean = false; // Track if current user has marked as viewed

  /**
   * Process comments from API response
   */
  private processComments(details: RoutingSlipDetails): void {
    this.comments = [];

    if (details.comments && details.comments.length > 0) {
      // Get unique user IDs from comments
      const uniqueUserIds = [
        ...new Set(details.comments.map((comment) => comment.createdBy)),
      ];

      // Load all user details at once
      this.loadUserDetailsForComments(uniqueUserIds, details.comments);
    }
  }

  /**
   * Load user details for all unique users in comments
   */
  private loadUserDetailsForComments(userIds: string[], comments: any[]): void {
    if (userIds.length === 0) return;

    // Load all user details in parallel
    const userRequests = userIds.map((userId) =>
      this.usersService.getUserById(userId).pipe(
        map((user) => ({ id: userId, name: user.fullName, email: user.email })),
        catchError(() => of({ id: userId, name: 'Unknown User', email: '' }))
      )
    );

    // Wait for all user details to load
    forkJoin(userRequests).subscribe((userDetails) => {
      // Cache all user details
      userDetails.forEach((user) => {
        this.userDetailsCache.set(user.id, {
          name: user.name,
          email: user.email,
        });
      });

      // Now process comments with resolved names
      comments.forEach((comment) => {
        this.comments.push({
          text: comment.comment,
          by:
            this.userDetailsCache.get(comment.createdBy)?.name ||
            'Unknown User',
          date: this.formatDate(comment.createdOn),
          time: this.formatTime(comment.createdOn),
        });
      });
    });
  }

  // Tailwind status pill helper
  statusPillClass(status?: string): string {
    const s = (status || 'Pending').toLowerCase();
    if (s === 'approved') return 'bg-green-100 text-green-700';
    if (s === 'rejected') return 'bg-red-100 text-red-700';
    if (s === 'reviewed') return 'bg-blue-100 text-blue-700';
    if (s === 'viewed') return 'bg-indigo-100 text-indigo-700';
    if (s === 'in progress' || s === 'in-progress') return 'bg-yellow-100 text-yellow-700';
    return 'bg-yellow-100 text-yellow-700';
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private routingSlipService: RoutingSlipService,
    private docusignService: DocusignService,
    private documentService: DocumentService,
    private usersService: UsersService,
    private authService: AuthService,
    private messageService: MessageService
  ) {}

  auditData: AuditsData[] = [];
  auditLoading: boolean = false;
  ngOnInit(): void {
    this.checkUserRole();
    this.setupDocuSignOpenerListener();
    if (this.beginDocuSignReturnTabIfNeeded()) {
      return;
    }
    this.consumeDocuSignSuccessToastIfAny();
    this.loadRoutingSlipDetails();
    // TODO: Replace with real audit API when available
    this.auditData = [];
  }

  ngOnDestroy(): void {
    this.clearDocuSignWindowPoll();
    if (this.docuSignOpenerMessageHandler) {
      window.removeEventListener('message', this.docuSignOpenerMessageHandler);
      this.docuSignOpenerMessageHandler = null;
    }
    if (this.docuSignStorageHandler) {
      window.removeEventListener('storage', this.docuSignStorageHandler);
      this.docuSignStorageHandler = null;
    }
  }

  /**
   * Same-origin DocuSign handoff: `postMessage` from return tab plus `storage` when `window.opener` is unset.
   */
  private setupDocuSignOpenerListener(): void {
    this.docuSignOpenerMessageHandler = (ev: MessageEvent) => {
      if (ev.origin !== window.location.origin) {
        return;
      }
      const d = ev.data;
      if (!d || typeof d !== 'object') {
        return;
      }
      if (d.type === 'docusign-approval-saved') {
        this.handleDocusignApproverejectSavedOnOpener(true);
        return;
      }
      if (
        d.type === 'docusign-approvereject-saved' ||
        d.type === 'routing-slip-approvereject-docusign-saved'
      ) {
        const isApproved =
          typeof (d as { isApproved?: boolean }).isApproved === 'boolean'
            ? (d as { isApproved: boolean }).isApproved
            : true;
        this.handleDocusignApproverejectSavedOnOpener(isApproved);
        return;
      }
      if (
        d.type === 'docusign-routing-slip-finished' &&
        d.terminal === 'fail'
      ) {
        const routeId = this.route.snapshot.queryParamMap.get('id');
        if (d.routingSlipId && routeId && d.routingSlipId !== routeId) {
          return;
        }
        this.handleDocuSignTerminalEventFromOpener(
          d.routingSlipId as string,
          d.docusignEvent as string | null
        );
      }
    };
    window.addEventListener('message', this.docuSignOpenerMessageHandler);

    this.docuSignStorageHandler = (ev: StorageEvent) => {
      if (ev.storageArea !== localStorage) {
        return;
      }
      if (ev.key !== this.docuSignApprovalSavedBroadcastKey || !ev.newValue) {
        return;
      }
      let routingSlipId: string | undefined;
      let isApprovedSaved: boolean | undefined;
      try {
        const parsed = JSON.parse(ev.newValue) as {
          routingSlipId?: string;
          isApproved?: boolean;
        };
        routingSlipId = parsed.routingSlipId;
        isApprovedSaved = parsed.isApproved;
      } catch {
        return;
      }
      const routeId = this.route.snapshot.queryParamMap.get('id');
      if (routingSlipId && routeId && routingSlipId !== routeId) {
        return;
      }
      this.handleDocusignApproverejectSavedOnOpener(
        typeof isApprovedSaved === 'boolean' ? isApprovedSaved : true
      );
    };
    window.addEventListener('storage', this.docuSignStorageHandler);
  }

  /**
   * Child tab has already called `POST .../approvereject`; refresh opener state only.
   */
  private handleDocusignApproverejectSavedOnOpener(isApproved: boolean): void {
    const t = Date.now();
    if (t - this.lastDocusignApprovalSavedHandledAt < 800) {
      return;
    }
    this.lastDocusignApprovalSavedHandledAt = t;

    this.showModal = false;
    this.isDocuSignSigningInProgress = false;
    this.isDocuSignApproveSubmitting = false;
    this.clearDocuSignWindowPoll();
    this.clearDocuSignSessionKeys();
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: isApproved
        ? 'Routing slip approved successfully.'
        : 'Routing slip rejected successfully.',
    });
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { event: 'signing_complete' },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.loadRoutingSlipDetails();
  }

  private clearDocuSignWindowPoll(): void {
    if (this.docuSignWindowPollId !== null) {
      clearInterval(this.docuSignWindowPollId);
      this.docuSignWindowPollId = null;
    }
  }

  /**
   * Polls the window reference opened for DocuSign. If the user closes the tab
   * without a successful return URL, sessionStorage still holds the pending id.
   */
  private startDocuSignWindowClosedPoll(
    signingWindow: Window,
    routingSlipId: string
  ): void {
    this.clearDocuSignWindowPoll();
    this.docuSignWindowPollId = setInterval(() => {
      if (!signingWindow.closed) {
        return;
      }
      this.clearDocuSignWindowPoll();
      if (this.getPendingDocuSignRoutingSlipId() === routingSlipId) {
        this.handleDocuSignTabClosedWithoutCompletion();
      }
    }, 1000);
  }

  /**
   * DocuSign return loads in a different tab: `sessionStorage` is not shared with the opener.
   * We mirror the same keys to `localStorage` so the redirect tab can validate and read the comment.
   */
  private setDocuSignKeyBoth(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
      localStorage.setItem(key, value);
    } catch {
      /* storage quota or disabled */
    }
  }

  private removeDocuSignKeyBoth(key: string): void {
    try {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }

  private clearDocuSignSessionKeys(): void {
    const pendingId = this.getPendingDocuSignRoutingSlipId();
    this.removeDocuSignKeyBoth(this.docuSignPendingStorageKey);
    this.removeDocuSignKeyBoth(this.docuSignEnvelopeIdStorageKey);
    this.removeDocuSignKeyBoth(this.docuSignPendingCommentStorageKey);
    this.removeDocuSignKeyBoth(this.docuSignPendingActionStorageKey);
    if (pendingId) {
      this.removeDocuSignKeyBoth(
        this.docuSignApprovalCommentStorageKeyForSlip(pendingId)
      );
    }
  }

  private getPendingDocuSignRoutingSlipId(): string | null {
    return (
      sessionStorage.getItem(this.docuSignPendingStorageKey) ||
      localStorage.getItem(this.docuSignPendingStorageKey)
    );
  }

  /** Approve/Reject modal text for `approvereject` after DocuSign (per-slip + legacy keys). */
  private docuSignApprovalCommentStorageKeyForSlip(slipId: string): string {
    return `${this.docuSignPendingCommentStorageKey}:${slipId}`;
  }

  /**
   * Comment from Approve or Reject modal (saved before DocuSign). Prefer per-slip key.
   */
  private getPendingApprovalCommentForDocusign(routingSlipId: string): string {
    if (routingSlipId) {
      const scoped = this.docuSignApprovalCommentStorageKeyForSlip(routingSlipId);
      const fromScoped =
        localStorage.getItem(scoped) ?? sessionStorage.getItem(scoped);
      if (fromScoped != null) {
        return fromScoped;
      }
    }
    const legacy =
      localStorage.getItem(this.docuSignPendingCommentStorageKey) ??
      sessionStorage.getItem(this.docuSignPendingCommentStorageKey) ??
      '';
    return legacy;
  }

  /** Whether POST should use `isApproved: true` or `false` (legacy flows without key ⇒ approve). */
  private getPendingApproverejectIsApprovedAfterDocusign(): boolean {
    const raw =
      localStorage.getItem(this.docuSignPendingActionStorageKey) ??
      sessionStorage.getItem(this.docuSignPendingActionStorageKey) ??
      '';
    if (raw === 'reject') return false;
    if (raw === 'approve') return true;
    return true;
  }

  private handleDocuSignTabClosedWithoutCompletion(): void {
    const envelopeId = (
      sessionStorage.getItem(this.docuSignEnvelopeIdStorageKey) ||
      localStorage.getItem(this.docuSignEnvelopeIdStorageKey)
    )?.trim();
    this.clearDocuSignSessionKeys();
    this.isDocuSignSigningInProgress = false;

    if (envelopeId) {
      this.docusignService.removeEnvelope(envelopeId).subscribe({
        next: (message) => {
          this.messageService.add({
            severity: 'success',
            summary: 'DocuSign',
            detail: message,
          });
          this.loadRoutingSlipDetails();
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error signing document. Please try again.',
          });
          this.loadRoutingSlipDetails();
        },
      });
      return;
    }

    this.messageService.add({
      severity: 'warn',
      summary: 'DocuSign',
      detail:
        'The signing window was closed before completion. You can use Approve and Sign and Submit again when you are ready.',
    });
    this.loadRoutingSlipDetails();
  }

  private handleDocuSignTerminalEventFromOpener(
    routingSlipId: string,
    docusignEvent: string | null
  ): void {
    const notifyKey = `${routingSlipId}:${docusignEvent ?? ''}`;
    const t = Date.now();
    if (
      this.lastDocuSignOpenerNotifyKey === notifyKey &&
      t - this.lastDocuSignOpenerNotifyTime < 500
    ) {
      return;
    }
    this.lastDocuSignOpenerNotifyKey = notifyKey;
    this.lastDocuSignOpenerNotifyTime = t;

    this.clearDocuSignSessionKeys();
    this.isDocuSignSigningInProgress = false;
    this.clearDocuSignWindowPoll();
    this.messageService.add({
      severity: 'info',
      summary: 'DocuSign',
      detail: this.messageForDocuSignTerminalEvent(docusignEvent),
    });
    this.loadRoutingSlipDetails();
  }

  private messageForDocuSignTerminalEvent(
    docusignEvent: string | null
  ): string {
    const e = (docusignEvent || '').toLowerCase().trim();
    switch (e) {
      case 'cancel':
        return 'Signing was cancelled in DocuSign. You can start again with Approve and Sign and Submit when you are ready.';
      case 'decline':
        return 'The document was declined in DocuSign.';
      case 'session_timeout':
        return 'The DocuSign session timed out. Please try again.';
      case 'ttl_expired':
        return 'The signing link has expired. Please try again.';
      case 'exception':
        return 'An error occurred in DocuSign. You can try signing again from Approve and Sign and Submit.';
      default:
        return 'Signing did not complete in DocuSign. You can try again from Approve and Sign and Submit when you are ready.';
    }
  }

  /** DocuSign return URL must include `event` (e.g. `signing_complete`); never infer success from a missing `event` (avoids spurious approvereject when opening details in a new tab with `window.opener` but no DocuSign redirect). */
  private isDocusignSigningCompleteQueryEvent(
    docusignEvent: string | null | undefined
  ): boolean {
    if (docusignEvent == null || String(docusignEvent).trim() === '') {
      return false;
    }
    const e = docusignEvent.toLowerCase().trim();
    return e === 'signing_complete' || e === 'complete';
  }

  /**
   * Apply GET routing-slip response to component state.
   */
  private applyLoadedRoutingSlipDetails(details: RoutingSlipDetails): void {
    this.routingSlipDetails = details;
    this.documentName = details.documentName || '';
    this.processAssignedUsers(details);
    this.processComments(details);
    this.resolveCurrentUserIdAndCheckPermissions(details);
  }

  /**
   * Ensure `userId` (GUID) is available via Users getuserdetail flow so assignment rows match on `userId`.
   * Login already populates it; this covers older sessions or failed lookup at sign-in.
   */
  private resolveCurrentUserIdAndCheckPermissions(
    details: RoutingSlipDetails
  ): void {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser?.email) {
      this.checkUserPermissions(details);
      return;
    }
    if (currentUser.id) {
      this.checkUserPermissions(details);
      return;
    }

    this.usersService.getUserDetailByEmail(currentUser.email).subscribe({
      next: (detail) => {
        if (detail?.id) {
          this.authService.setStoredUserId(detail.id);
        }
        this.checkUserPermissions(details);
      },
      error: () => this.checkUserPermissions(details),
    });
  }

  /**
   * DocuSign return URL: read `id` and `event`, POST approvereject on success, or notify opener /
   * close on failure endpoints. Does **not** require `window.opener` (often null after DocuSign
   * redirects). Pending id must match session/local storage (`setDocuSignKeyBoth` from opener).
   */
  private beginDocuSignReturnTabIfNeeded(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const routingSlipId = this.route.snapshot.queryParamMap.get('id');
    if (!routingSlipId) {
      return false;
    }
    const docusignEvent =
      this.route.snapshot.queryParamMap.get('event') ??
      this.route.snapshot.queryParamMap.get('dsevent');
    if (docusignEvent == null || String(docusignEvent).trim() === '') {
      return false;
    }
    if (this.getPendingDocuSignRoutingSlipId() !== routingSlipId) {
      return false;
    }

    // The return tab POSTs approvereject and then navigates this (opener) tab to the
    // same return URL. It writes the success handoff flag to shared localStorage *before*
    // navigating, so if that flag is present this tab is the opener — the document was
    // already actioned. Clear our own (per-tab) pending key and let the normal opener flow
    // (success toast + refresh) run instead of re-POSTing, which would 400 ("User already
    // worked on this document").
    if (this.hasPendingDocuSignSuccessForSlip(routingSlipId)) {
      this.clearDocuSignSessionKeys();
      return false;
    }

    // Return-tab flow: suppress "not found" UI while POST /approvereject or notify+close runs.
    this.loading = true;

    if (this.isDocusignSigningCompleteQueryEvent(docusignEvent)) {
      this.submitApproveRejectAfterDocusignSigningComplete(routingSlipId);
      return true;
    }

    this.notifyOpenerOfDocuSignFailure(routingSlipId, docusignEvent);
    setTimeout(() => {
      this.clearDocuSignSessionKeys();
      this.tryCloseDocuSignReturnTab();
    }, 200);
    return true;
  }

  /**
   * DocuSign `event=signing_complete`: **`POST .../RoutingSlip/{id}/approvereject`** runs here in the
   * window that loads the return URL, immediately on bootstrap — before notifying the opener tab.
   * Body: `{ isApproved, comment }` from stored pending action (Approve vs Reject) and modal comment.
   */
  private submitApproveRejectAfterDocusignSigningComplete(
    routingSlipId: string
  ): void {
    const comment = this.getPendingApprovalCommentForDocusign(routingSlipId);
    const isApproved = this.getPendingApproverejectIsApprovedAfterDocusign();
    this.routingSlipService
      .approveRejectRoutingSlip(routingSlipId, isApproved, comment)
      .subscribe({
        next: () => {
          this.clearDocuSignSessionKeys();
          const returnHref = window.location.href;
          const op = window.opener;
          let openerIsOurApp = false;
          if (op && !op.closed) {
            try {
              openerIsOurApp =
                op.location.origin === window.location.origin;
            } catch {
              /* cross-origin opener (e.g. DocuSign) */
              openerIsOurApp = false;
            }
          }
          if (openerIsOurApp && op && !op.closed) {
            try {
              this.setDocuSignSuccessToastForParent(routingSlipId, isApproved);
              op.focus();
              op.location.replace(returnHref);
            } catch {
              this.fallbackNotifyParentDocuSignApproverejectSaved(
                routingSlipId,
                isApproved
              );
            }
          } else {
            this.fallbackNotifyParentDocuSignApproverejectSaved(
              routingSlipId,
              isApproved
            );
          }
          this.tryCloseDocuSignReturnTab();
          this.scheduleDocuSignReturnTabLoadingReleaseIfStillOpen();
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.extractErrorMessage(error),
          });
          this.clearDocuSignSessionKeys();
          this.loadRoutingSlipDetails();
        },
      });
  }

  /**
   * `window.close()` can fail to run on slow connections until after paint;
   * retry a few times so the return tab does not stay open unnecessarily.
   */
  private tryCloseDocuSignReturnTab(): void {
    const tryOnce = (): void => {
      try {
        window.close();
      } catch {
        /* ignore */
      }
    };
    tryOnce();
    const delaysMs = [50, 150, 400, 1000, 2500];
    delaysMs.forEach((ms) => {
      setTimeout(() => {
        if (!window.closed) {
          tryOnce();
        }
      }, ms);
    });
  }

  /** When return tab cannot `location.replace` the opener (e.g. no opener); parent updates in-place. */
  private fallbackNotifyParentDocuSignApproverejectSaved(
    routingSlipId: string,
    isApproved: boolean
  ): void {
    const payload = {
      type: 'docusign-approvereject-saved' as const,
      routingSlipId,
      isApproved,
    };
    try {
      window.opener?.postMessage(payload, window.location.origin);
    } catch {
      /* ignore */
    }
    this.broadcastDocusignApproverejectSavedToOpenerTab(
      routingSlipId,
      isApproved
    );
  }

  private setDocuSignSuccessToastForParent(
    routingSlipId: string,
    isApproved: boolean
  ): void {
    try {
      localStorage.setItem(
        this.docuSignSuccessToastStorageKey,
        JSON.stringify({ routingSlipId, isApproved, t: Date.now() })
      );
    } catch {
      /* storage disabled */
    }
  }

  /**
   * True when the return tab has already saved approvereject for this slip and handed a
   * success toast to the opener via localStorage. Peeks the flag without consuming it
   * (consumeDocuSignSuccessToastIfAny removes it and shows the toast).
   */
  private hasPendingDocuSignSuccessForSlip(routingSlipId: string): boolean {
    try {
      const raw = localStorage.getItem(this.docuSignSuccessToastStorageKey);
      if (!raw) {
        return false;
      }
      const parsed = JSON.parse(raw) as { routingSlipId?: string };
      return !!parsed.routingSlipId && parsed.routingSlipId === routingSlipId;
    } catch {
      return false;
    }
  }

  private consumeDocuSignSuccessToastIfAny(): void {
    try {
      const raw = localStorage.getItem(this.docuSignSuccessToastStorageKey);
      if (!raw) {
        return;
      }
      localStorage.removeItem(this.docuSignSuccessToastStorageKey);
      const parsed = JSON.parse(raw) as {
        routingSlipId?: string;
        isApproved?: boolean;
      };
      const routeId = this.route.snapshot.queryParamMap.get('id');
      if (parsed.routingSlipId && routeId && parsed.routingSlipId !== routeId) {
        return;
      }
      const isApproved =
        typeof parsed.isApproved === 'boolean' ? parsed.isApproved : true;
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: isApproved
          ? 'Routing slip approved successfully.'
          : 'Routing slip rejected successfully.',
      });
    } catch {
      /* ignore */
    }
  }

  /**
   * If the browser will not close this window (common when the return URL opened in a tab not created via `window.open`), avoid an infinite loading state.
   */
  private scheduleDocuSignReturnTabLoadingReleaseIfStillOpen(): void {
    setTimeout(() => {
      if (typeof window !== 'undefined' && !window.closed && this.loading) {
        this.loading = false;
      }
    }, 3200);
  }

  /** Notifies opener when `window.opener` is unavailable (fires `storage` only in other tabs). */
  private broadcastDocusignApproverejectSavedToOpenerTab(
    routingSlipId: string,
    isApproved: boolean
  ): void {
    try {
      localStorage.setItem(
        this.docuSignApprovalSavedBroadcastKey,
        JSON.stringify({ routingSlipId, t: Date.now(), isApproved })
      );
    } catch {
      /* storage disabled */
    }
  }

  private notifyOpenerOfDocuSignFailure(
    routingSlipId: string,
    docusignEvent: string | null
  ): void {
    const payload = {
      type: 'docusign-routing-slip-finished',
      terminal: 'fail' as const,
      routingSlipId,
      docusignEvent: docusignEvent ?? null,
    };
    try {
      window.opener?.postMessage(payload, window.location.origin);
    } catch {
      /* ignore */
    }
  }

  private checkUserRole(): void {
    const currentUser = this.authService.getCurrentUser();

    // Set userProfile from authenticated user
    if (currentUser) {
      this.userProfile = {
        id: currentUser.id ?? currentUser.email,
        name: currentUser.name,
        email: currentUser.email,
        role: currentUser.role,
        avatar: 'assets/images/logo.png',
      };
    }

    this.isManager = this.authService.hasRole(UserRole.MANAGER);
    this.isSecretary = this.authService.hasRole(UserRole.SECRETARY);
    this.isAdmin = this.authService.hasRole(UserRole.ADMIN);

    // Hide Archive button for Manager role only
    this.showArchiveButton = !this.isManager;
  }

  private loadRoutingSlipDetails(): void {
    this.loading = true;

    // Get the ID and archived status from query params
    const id = this.route.snapshot.queryParamMap.get('id');
    const archived = this.route.snapshot.queryParamMap.get('archived');

    // Check if document is archived from query params (when navigating from archive list)
    // Hide Archive button if document is archived (role-based hiding is already handled in checkUserRole)
    if (archived === 'true') {
      this.showArchiveButton = false;
    }

    if (!id) {
      this.loading = false;
      return;
    }

    this.routingSlipService.getRoutingSlipById(id).subscribe({
      next: (details) => {
        this.applyLoadedRoutingSlipDetails(details);
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.routingSlipDetails = null;
        this.documentName = '';
        this.showAuditButton = false;
        this.showArchiveButton = false;
        this.showApproveButton = false;
        this.showRejectButton = false;
        this.showCommentButton = false;
        this.showMarkAsViewedButton = false;
        if (error?.status === 403) {
          this.loadErrorIcon = 'pi-lock';
          this.loadErrorTitle = 'Access denied';
          this.loadErrorMessage =
            'You do not have permission to view this routing slip.';
        } else {
          this.loadErrorIcon = 'pi-file';
          this.loadErrorTitle = 'Routing slip not found';
          this.loadErrorMessage = 'This routing slip is not available.';
        }
      },
    });
  }

  /**
   * Resolve the assignment row for the signed-in user (userId when available, else email).
   */
  private findAssignmentForCurrentUser(
    details: RoutingSlipDetails,
    currentUser: { id?: string; email: string }
  ): (typeof details.assignedUsers)[0] | undefined {
    const assignments = details.assignedUsers;
    if (!assignments?.length) return undefined;

    if (currentUser.id) {
      const byUserId = assignments.find((u) => u.userId === currentUser.id);
      if (byUserId) return byUserId;
    }

    const emailLower = currentUser.email?.toLowerCase();
    return assignments.find(
      (u) => u.email?.toLowerCase() === emailLower
    );
  }

  /**
   * Hide Approve/Reject when this assignee's step is already decided (API `status` on the assignment).
   */
  private applyCurrentUserAssignmentStepToApproveRejectButtons(
    assignedUser: { status?: string }
  ): void {
    const stepStatus = this.mapStatusToReadable(assignedUser.status ?? '');
    if (stepStatus === 'Approved' || stepStatus === 'Rejected') {
      this.showApproveButton = false;
      this.showRejectButton = false;
    }
  }

  /**
   * Check user permissions and set button visibility
   */
  private checkUserPermissions(details: RoutingSlipDetails): void {
    // Get current user email from auth service
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser || !currentUser.email) {
      // If no current user, hide all action buttons except audit and archive
      this.setButtonVisibility(false, false, false);
      return;
    }

    const assignedUser = this.findAssignmentForCurrentUser(details, currentUser);

    if (!assignedUser) {
      // If user is not assigned, hide all action buttons except audit and archive
      this.setButtonVisibility(false, false, false);
      return;
    }

    // Prefer statusDescription from API (e.g. "Viewed") over numeric approveRejectStatus
    const statusSource = assignedUser.status ?? details.statusDescription ?? details.approveRejectStatus;
    const userStatus = this.mapStatusToReadable(statusSource);
    this.isMarkedAsViewed = userStatus === 'Viewed';

    // Set permission and button visibility based on permission value
    this.userPermission = assignedUser.permission;
    this.setButtonVisibilityBasedOnPermission(assignedUser.permission);
    this.applySlipFinalStatusToApproveRejectButtons(details);
    this.applyCurrentUserAssignmentStepToApproveRejectButtons(assignedUser);
  }

  /**
   * When GET routing slip reports a final decision on the slip (e.g. statusDescription "Approved"),
   * Approve/Reject must not be shown regardless of assignee permission.
   */
  private applySlipFinalStatusToApproveRejectButtons(
    details: RoutingSlipDetails
  ): void {
    const slipStatus = this.mapStatusToReadable(
      details.statusDescription ?? details.approveRejectStatus
    );
    if (slipStatus === 'Approved' || slipStatus === 'Rejected') {
      this.showApproveButton = false;
      this.showRejectButton = false;
    }
  }

  /**
   * Set button visibility based on permission
   */
  private setButtonVisibilityBasedOnPermission(permission: number): void {
    switch (permission) {
      case 1: // ViewOnly
        // Show "Mark as Viewed" button only for Secretary and Manager (NOT Admin)
        this.showMarkAsViewedButton = (this.isSecretary || this.isManager) && !this.isAdmin;
        this.setButtonVisibility(false, false, false);
        break;
      case 2: // ApproveReject
        this.showMarkAsViewedButton = false; // Hide for ApproveReject permission
        this.setButtonVisibility(true, true, true);
        break;
      default:
        // Unknown permission, hide all action buttons
        this.showMarkAsViewedButton = false;
        this.setButtonVisibility(false, false, false);
        break;
    }
  }

  /**
   * Set button visibility
   */
  private setButtonVisibility(
    showComment: boolean,
    showApprove: boolean,
    showReject: boolean
  ): void {
    this.showCommentButton = showComment;
    this.showApproveButton = showApprove;
    this.showRejectButton = showReject;
  }

  /**
   * Process assigned users from API response
   */
  private processAssignedUsers(details: RoutingSlipDetails): void {
    this.assignedUsers = [];
    this.assignedUserLevels = [];

    if (!details.assignedUsers || details.assignedUsers.length === 0) return;

    // Backward-compatible flat list (used by legacy template areas, if any)
    details.assignedUsers.forEach((user) => {
      const statusSource =
        user.status ?? details.statusDescription ?? details.approveRejectStatus;
      this.assignedUsers.push({
        name: user.fullName || user.name || 'Unknown User',
        status: this.mapStatusToReadable(statusSource),
        date: this.formatDate(details.createdOn),
        time: this.formatTime(details.createdOn),
      });
    });

    // Grouped view: levels stacked, users side-by-side within each level.
    // Use API `level` for grouping and `stepOrder` for ordering inside each level.
    const byLevel = new Map<
      number,
      {
        permissionValues: Set<number>;
        users: Array<{
          name: string;
          status: 'Approved' | 'Rejected' | 'Pending' | 'Viewed';
          date: string;
          time: string;
          stepOrder: number;
          comment?: string;
        }>;
      }
    >();

    details.assignedUsers.forEach((user) => {
      const level = Number((user as any).level ?? 1);
      const permissionValue = Number(user.permission || 0);
      const stepOrder = Number(user.stepOrder || 0);
      const statusSource =
        user.status ?? details.statusDescription ?? details.approveRejectStatus;
      const actionOn = (user as any).actionOn;
      const actionComment = (user as any).actionComment;

      const entry =
        byLevel.get(level) ?? {
          permissionValues: new Set<number>(),
          users: [],
        };

      entry.permissionValues.add(permissionValue);
      entry.users.push({
        name: user.fullName || user.name || 'Unknown User',
        status: this.mapStatusToReadable(statusSource),
        date: this.formatDate(actionOn || details.createdOn),
        time: this.formatTime(actionOn || details.createdOn),
        stepOrder,
        comment: actionComment || undefined,
      });

      byLevel.set(level, {
        permissionValues: entry.permissionValues,
        users: entry.users,
      });
    });

    this.assignedUserLevels = Array.from(byLevel.entries())
      .sort(([a], [b]) => a - b)
      .map(([level, data]) => {
        const permissionValues = Array.from(data.permissionValues).filter(
          (v) => !Number.isNaN(v) && v !== 0
        );

        const permissionLabel =
          permissionValues.length === 0
            ? '—'
            : permissionValues
                .map((p) => this.getPermissionLabel(p))
                .filter(Boolean)
                .join(', ');

        return {
          level,
          permissionLabel,
          permissionValues,
          users: data.users.sort((a, b) => a.stepOrder - b.stepOrder),
        };
      });
  }

  getPermissionLabel(permissionValue: number): string {
    switch (permissionValue) {
      case 1:
        return 'View Only';
      case 2:
        return 'Approve/Reject';
      default:
        return `Permission ${permissionValue}`;
    }
  }

  /**
   * Map numeric or string status to readable string
   */
  private mapStatusToReadable(
    status: number | string
  ): 'Approved' | 'Rejected' | 'Pending' | 'Viewed' {
    if (typeof status === 'string') {
      // Handle string status values from API
      const statusLower = status.toLowerCase();
      if (statusLower === 'approved') return 'Approved';
      if (statusLower === 'rejected') return 'Rejected';
      if (statusLower === 'pending') return 'Pending';
      if (statusLower === 'viewed') return 'Viewed';
      return 'Pending';
    }

    // Handle numeric status values
    switch (status) {
      case 1:
        return 'Approved';
      case 2:
        return 'Rejected';
      case 3:
        return 'Pending';
      default:
        return 'Pending';
    }
  }

  /**
   * Map review status to readable string
   */
  private mapReviewStatusToReadable(status: number): string {
    switch (status) {
      case 1:
        return 'Pending';
      case 2:
        return 'In Progress';
      case 3:
        return 'Completed';
      default:
        return 'Pending';
    }
  }

  /**
   * Format date string to DD-MM-YYYY format
   */
  private formatDateInternal(dateString: string): string {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  /**
   * Format time string to HH:MM format
   */
  private formatTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Get user name from cache or fetch from API
   */
  getUserName(userId: string): string {
    if (!userId) return 'Unknown User';

    if (this.userDetailsCache.has(userId)) {
      return this.userDetailsCache.get(userId)!.name;
    }

    // Fetch user details if not in cache
    this.usersService.getUserById(userId).subscribe({
      next: (user) => {
        this.userDetailsCache.set(userId, {
          name: user.fullName,
          email: user.email,
        });
      },
      error: (error) => {
        // Set a fallback name in cache to avoid repeated API calls
        this.userDetailsCache.set(userId, { name: 'Unknown User', email: '' });
      },
    });

    return 'Unknown User';
  }

  /**
   * Get status text for display
   */
  getStatusText(status?: number): string {
    if (status === undefined || status === null) return 'Pending';
    return this.mapStatusToReadable(status);
  }

  /**
   * Get status description text for display (from statusDescription field).
   * Maps statusDescription values: "Pending" -> "In Progress", "Viewed" -> "Viewed"
   */
  getStatusDescriptionText(statusDescription?: string): string {
    if (!statusDescription) return 'Pending';
    
    const statusLower = statusDescription.toLowerCase().trim();
    
    // Map "Pending" to "In Progress"
    if (statusLower === 'pending') {
      return 'In Progress';
    }
    
    // Normalize "In-Progress" or "In Progress" to "In Progress"
    if (statusLower === 'in-progress' || statusLower === 'in progress') {
      return 'In Progress';
    }
    
    // Keep "Viewed" as is
    if (statusLower === 'viewed') {
      return 'Viewed';
    }
    
    // For other statuses, return as-is (capitalize first letter of each word)
    return statusDescription
      .split(/[\s-]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Get review status text for display
   */
  getReviewStatusText(status?: number): string {
    if (status === undefined || status === null) return 'Pending';
    return this.mapReviewStatusToReadable(status);
  }

  /**
   * Format date for display (public method for template)
   */
  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';
    return this.formatDateInternal(dateString);
  }

  /** Manager & Secretary use DocuSign for approval; Admin uses direct API only. */
  /** Manager/Secretary run Approve through DocuSign, then `POST .../approvereject`. Reject does not. */
  private get useDocuSignForApprovereject(): boolean {
    return !this.isAdmin && (this.isManager || this.isSecretary);
  }

  /**
   * Approve modal is open in DocuSign “Sign and Submit” mode (keep modal open until URL is ready).
   */
  get isDocuSignSignAndSubmitModalMode(): boolean {
    return this.modalType === 'approve' && this.useDocuSignForApprovereject;
  }

  /** Pre-fill comment from storage (Approve or Reject modal before DocuSign). */
  get pendingDocusignApproverejectCommentForModal(): string {
    const id =
      this.route.snapshot.queryParamMap.get('id') ??
      this.routingSlipDetails?.id ??
      '';
    return this.getPendingApprovalCommentForDocusign(id);
  }

  // Modal methods
  openCommentModal(): void {
    this.modalType = 'comment';
    this.modalTitle = 'Add New Comment:';
    this.modalSubmitButtonLabel = 'Submit';
    this.showModal = true;
  }

  openUploadModal(): void {
    this.modalType = 'upload';
    this.modalTitle = 'Upload Document';
    this.showModal = true;
  }

  onModalClose(): void {
    this.isDocuSignApproveSubmitting = false;
    this.showModal = false;
  }

  onModalSubmit(data: any): void {
    if (this.modalType === 'comment') {
      this.handleCommentSubmit(data);
    } else if (this.modalType === 'upload') {
      this.handleUploadSubmit(data);
    } else if (this.modalType === 'approve') {
      this.handleApproveSubmit(data);
    } else if (this.modalType === 'reject') {
      this.handleRejectSubmit(data);
    }
  }

  private handleCommentSubmit(data: any): void {
    if (!this.routingSlipDetails?.id) {
      return;
    }

    // Pass the comment string directly to the API
    this.routingSlipService
      .addComment(this.routingSlipDetails.id, data.comment)
      .subscribe({
        next: (newComment: Comment) => {
          // Load user details for the new comment
          this.loadUserDetailsForNewComment(newComment);
        },
        error: (error) => {
          // Show error toaster
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add comment. Please try again.',
          });
        },
      });
  }

  private handleUploadSubmit(data: any): void {
    if (!data.files || data.files.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No file selected for upload.',
      });
      return;
    }

    const file = data.files[0];

    // Validate file type (only PDF and DOC/DOCX)
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (!allowedTypes.includes(file.type)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Only PDF and DOC/DOCX files are allowed.',
      });
      return;
    }

    // Validate file size (15MB = 15 * 1024 * 1024 bytes)
    const maxSize = 15 * 1024 * 1024; // 15MB in bytes
    if (file.size > maxSize) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'File size must not exceed 15MB.',
      });
      return;
    }

    if (!this.routingSlipDetails?.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No routing slip ID available for upload.',
      });
      return;
    }

    // Call the upload API
    this.routingSlipService
      .uploadRoutingSlipDocument(this.routingSlipDetails.id, file)
      .subscribe({
        next: (response) => {
          // Create document attachment for local storage
          const attachment: DocumentAttachment = {
            name: file.name,
            size: this.formatFileSize(file.size),
            type: file.type,
            // fileData is optional, we don't store the file data locally anymore
            lastModified: file.lastModified,
          };

          // Store the uploaded document reference
          this.currentDocument = attachment;

          // Update routing slip details with the new attachment
          if (this.routingSlipDetails) {
            this.routingSlipDetails.attachments = [attachment];
            this.routingSlipDetails.documentName = file.name;
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Document uploaded successfully.',
          });

          // Close the modal
          this.showModal = false;
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to upload document. Please try again.',
          });
        },
      });
  }

  onBack(): void {
    this.router.navigate(['/routing-slip']);
  }
  selectedRow: any;
  auditDialogVisible: boolean = false;
  onViewAuditsLog(): void {
    if (!this.routingSlipDetails?.id) {
      return;
    }

    // Reset audit data and set loading state
    this.auditData = [];
    this.auditLoading = true;
    this.selectedRow = this.routingSlipDetails;
    this.auditDialogVisible = true;

    // Load audit logs from API
    this.routingSlipService.getAuditLog(this.routingSlipDetails.id).subscribe({
      next: (auditLogs) => {
        // Transform the API response to match the expected AuditsData structure
        this.auditData = (auditLogs || []).map((log: any) => ({
          date: this.formatDate(log.createdOn),
          time: this.formatTime(log.createdOn),
          user: log.userName,
          action: log.userAction,
        }));

        this.auditLoading = false;
      },
      error: (error) => {
        this.auditLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load audit logs. Please try again.',
          life: 5000,
        });
      },
    });
  }

  onAuditClose(): void {
    this.auditDialogVisible = false;
    this.auditLoading = false;
  }

  onDelete(): void {
    // Show confirmation modal
    this.showDeleteModal = true;
  }

  confirmDeleteRoutingSlip(): void {
    // Double check that routing slip ID exists
    if (!this.routingSlipDetails?.id) {
      this.cancelDeleteRoutingSlip();
      return;
    }

    // Prevent multiple deletion attempts
    if (this.isDeleting) {
      return;
    }

    // Set deleting state to show loading feedback
    this.isDeleting = true;

    this.routingSlipService
      .deleteRoutingSlip(this.routingSlipDetails.id)
      .subscribe({
        next: (response) => {
          // Show success toast
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: response.message || 'Routing slip deleted successfully.',
            life: 4000,
          });

          // Reset delete modal state first
          this.showDeleteModal = false;
          this.isDeleting = false;

          // Navigate back to routing slip list
          this.router.navigate(['/routing-slip']);
        },
        error: (error) => {
          // Show error toast
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Something went wrong. Please try again.',
            life: 5000,
          });

          // Reset delete modal state
          this.showDeleteModal = false;
          this.isDeleting = false;
        },
      });
  }

  cancelDeleteRoutingSlip(): void {
    this.showDeleteModal = false;
    this.isDeleting = false;
  }

  // Manager-specific button methods
  onComment(): void {
    this.modalType = 'comment';
    this.modalTitle = 'Comment:';
    this.modalSubmitButtonLabel = 'Submit';
    this.showModal = true;
  }

  onApprove(): void {
    if (this.warnIfEarlierLevelIsPending()) {
      return;
    }
    this.modalType = 'approve';
    this.modalTitle = 'Reason of Approval:';
    this.modalSubmitButtonLabel = this.useDocuSignForApprovereject
      ? 'Sign and Submit'
      : 'Submit';
    this.showModal = true;
  }

  onReject(): void {
    if (this.warnIfEarlierLevelIsPending()) {
      return;
    }
    this.modalType = 'reject';
    this.modalTitle = 'Reason of Rejection:';
    this.modalSubmitButtonLabel = 'Submit';
    this.showModal = true;
  }

  /**
   * A later level must not open the approval note while an earlier level is still pending.
   * Returns true when the warning was shown.
   */
  private warnIfEarlierLevelIsPending(): boolean {
    if (
      !isEarlierRoutingLevelPending(
        this.routingSlipDetails,
        this.authService.getCurrentUser()
      )
    ) {
      return false;
    }
    this.messageService.add({
      severity: 'warn',
      summary: 'Please wait',
      detail: WAIT_FOR_PREVIOUS_USER_MESSAGE,
    });
    return true;
  }

  onMarkAsViewed(): void {
    if (!this.routingSlipDetails?.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No routing slip ID available.',
      });
      return;
    }

    this.routingSlipService.markAsViewed(this.routingSlipDetails.id).subscribe({
      next: (response) => {
        // Update the button state immediately
        this.isMarkedAsViewed = true;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Status updated to Viewed successfully',
        });
        // Reload the routing slip details to show updated status
        this.loadRoutingSlipDetails();
      },
      error: (error) => {
        // Extract the specific error message from the backend response
        const errorMessage = this.extractErrorMessage(error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
        });
      },
    });
  }

  /**
   * Opens DocuSign in a new tab; comment and approve/reject intent are stored for the return URL
   * to call `POST .../approvereject` with `{ isApproved, comment }`.
   */
  private submitDocuSignRoutingSlipApprovereject(
    isApproved: boolean,
    data: { comment?: string }
  ): void {
    if (!this.routingSlipDetails?.id) {
      return;
    }
    this.isDocuSignApproveSubmitting = true;
    const text = typeof data?.comment === 'string' ? data.comment : '';
    const slipId = this.routingSlipDetails.id;
    this.setDocuSignKeyBoth(
      this.docuSignApprovalCommentStorageKeyForSlip(slipId),
      text
    );
    this.setDocuSignKeyBoth(this.docuSignPendingCommentStorageKey, text);
    this.setDocuSignKeyBoth(
      this.docuSignPendingActionStorageKey,
      isApproved ? 'approve' : 'reject'
    );
    const documentId =
      this.route.snapshot.queryParamMap.get('id') || this.routingSlipDetails.id;
    this.docusignService
      .addDocusignSignature(documentId, DocusignModule.RoutingSlipDetails)
      .subscribe({
        next: (signing) => {
          const url = signing.signingUrl?.trim();
          if (!url) {
            this.isDocuSignApproveSubmitting = false;
            this.removeDocuSignKeyBoth(this.docuSignPendingCommentStorageKey);
            this.removeDocuSignKeyBoth(
              this.docuSignApprovalCommentStorageKeyForSlip(slipId)
            );
            this.removeDocuSignKeyBoth(this.docuSignPendingActionStorageKey);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail:
                'No signing URL was returned from the server. Please try again.',
            });
            return;
          }
          this.isDocuSignApproveSubmitting = false;
          this.showModal = false;
          this.setDocuSignKeyBoth(
            this.docuSignPendingStorageKey,
            this.routingSlipDetails!.id
          );
          const envelopeId = signing.envelopeId?.trim();
          if (envelopeId) {
            this.setDocuSignKeyBoth(
              this.docuSignEnvelopeIdStorageKey,
              envelopeId
            );
          } else {
            this.removeDocuSignKeyBoth(this.docuSignEnvelopeIdStorageKey);
          }
          const tab = window.open(url, '_blank');
          if (!tab) {
            this.clearDocuSignSessionKeys();
            this.isDocuSignSigningInProgress = false;
            this.messageService.add({
              severity: 'warn',
              summary: 'Popup blocked',
              detail:
                'Allow pop-ups for this site to open DocuSign in a new tab.',
            });
            return;
          }
          this.isDocuSignSigningInProgress = true;
          this.startDocuSignWindowClosedPoll(
            tab,
            this.routingSlipDetails!.id
          );
          this.messageService.add({
            severity: 'info',
            summary: 'DocuSign',
            detail: 'Complete signing in the new tab. This tab stays open.',
          });
          this.loadRoutingSlipDetails();
        },
        error: (error) => {
          this.isDocuSignApproveSubmitting = false;
          this.removeDocuSignKeyBoth(this.docuSignPendingCommentStorageKey);
          this.removeDocuSignKeyBoth(
            this.docuSignApprovalCommentStorageKeyForSlip(
              this.routingSlipDetails!.id
            )
          );
          this.removeDocuSignKeyBoth(this.docuSignPendingActionStorageKey);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail:
              this.extractErrorMessage(error) ||
              'Error signing document. Please try again.',
          });
          this.loadRoutingSlipDetails();
        },
      });
  }

  private handleApproveSubmit(data: any): void {
    if (!this.routingSlipDetails?.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No routing slip ID available for approval.',
      });
      return;
    }

    // Manager/Secretary: DocuSign addsignature first (same as Reject); approvereject runs after return URL.
    if (this.useDocuSignForApprovereject) {
      this.submitDocuSignRoutingSlipApprovereject(true, data);
      return;
    }

    this.routingSlipService
      .approveRejectRoutingSlip(this.routingSlipDetails.id, true, data.comment)
      .subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Routing slip approved successfully',
          });
          this.loadRoutingSlipDetails();
        },
        error: (error) => {
          const errorMessage = this.extractErrorMessage(error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
          });
        },
      });
  }

  private handleRejectSubmit(data: any): void {
    if (!this.routingSlipDetails?.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No routing slip ID available for rejection.',
      });
      return;
    }

    this.routingSlipService
      .approveRejectRoutingSlip(this.routingSlipDetails.id, false, data.comment)
      .subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Routing slip rejected successfully',
          });
          // Reload the routing slip details to show updated status
          this.loadRoutingSlipDetails();
        },
        error: (error) => {
          // Extract the specific error message from the backend response
          const errorMessage = this.extractErrorMessage(error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
          });
        },
      });
  }

  /**
   * Opens the viewer with the file from `GET /api/Docusign/download/{documentId}`.
   * If the API returns `success: false` (e.g. envelope still "sent"), falls back to
   * `GET /api/RoutingSlip/{id}/download`.
   */
  onViewDocument(): void {
    const documentId =
      this.route.snapshot.queryParamMap.get('id') ||
      this.routingSlipDetails?.id;
    if (!documentId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No routing slip ID available for document download.',
      });
      return;
    }

    this.showDocumentViewer = true;
    this.currentDocument = null;

    this.docusignService
      .getDownloadResponse(documentId)
      .pipe(
        switchMap((res) => {
          if (res && res.success === false) {
            return this.routingSlipService.getRoutingSlipDocumentForViewer(
              documentId
            );
          }
          try {
            return of({
              kind: 'docusign' as const,
              data: this.docusignService.parseDocusignFilePayload(res),
            });
          } catch (err) {
            return throwError(() =>
              err instanceof Error ? err : new Error(String(err))
            );
          }
        })
      )
      .subscribe({
        next: (payload) => {
          if ('kind' in payload && payload.kind === 'docusign') {
            const { blob, fileName: apiFileName } = payload.data;
            const fileName =
              apiFileName ||
              this.routingSlipDetails?.uploadedFileName ||
              this.routingSlipDetails?.documentName ||
              'document.pdf';
            const type =
              blob.type && blob.type !== 'application/octet-stream'
                ? blob.type
                : this.getFileTypeFromName(fileName);
            const objectUrl = window.URL.createObjectURL(blob);
            this.currentDocument = {
              name: fileName,
              size: this.formatFileSize(blob.size),
              type: type || 'application/pdf',
              fileData: objectUrl,
              lastModified: Date.now(),
              blobUrl: objectUrl,
              originalBlob: blob,
            };
            return;
          }

          if (payload.kind === 'blob') {
            const objectUrl = window.URL.createObjectURL(payload.blob);
            this.currentDocument = {
              name: payload.filename,
              size: this.formatFileSize(payload.blob.size),
              type: payload.type,
              fileData: objectUrl,
              lastModified: Date.now(),
              blobUrl: objectUrl,
              originalBlob: payload.blob,
            };
            return;
          }

          this.currentDocument = {
            name: payload.filename,
            size: '-',
            type: payload.type,
            url: payload.url,
            lastModified: Date.now(),
          };
        },
        error: (error) => {
          this.showDocumentViewer = false;
          const detail = this.extractErrorMessage(error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: detail || 'Failed to load document. Please try again.',
          });
        },
      });
  }

  /**
   * Get file type from file name extension
   * @param fileName The file name
   * @returns The MIME type
   */
  private getFileTypeFromName(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'application/pdf';
      case 'doc':
        return 'application/msword';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'txt':
        return 'text/plain';
      default:
        return 'application/octet-stream';
    }
  }

  /**
   * Format file size in human readable format
   * @param bytes File size in bytes
   * @returns Formatted file size string
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Document viewer modal methods
  onDocumentViewerClose(): void {
    // Clean up blob URL to prevent memory leaks
    if (this.currentDocument?.blobUrl) {
      window.URL.revokeObjectURL(this.currentDocument.blobUrl);
    }
    this.showDocumentViewer = false;
    this.currentDocument = null;
  }

  onDocumentDownload(attachment: DocumentAttachment): void {
    const remote = attachment.url?.trim();
    if (remote) {
      window.open(remote, '_blank');
      return;
    }

    // If we have the original blob from the API, use it directly
    if (attachment.originalBlob) {
      // Create download link
      const url = window.URL.createObjectURL(attachment.originalBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.name;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // this.messageService.add({
      //   severity: 'success',
      //   summary: 'Success',
      //   detail: 'Document downloaded successfully.',
      // });
    } else if (this.routingSlipDetails?.id) {
      // If no cached blob but we have routing slip ID, fetch from API
      this.routingSlipService
        .downloadRoutingSlipDocument(this.routingSlipDetails.id)
        .subscribe({
          next: (response: { blob: Blob; filename: string; type: string }) => {
            // Create download link
            const url = window.URL.createObjectURL(response.blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = response.filename;

            // Trigger download
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Document downloaded successfully.',
            });
          },
          error: (error) => {
            // Fallback to local document service
            this.documentService.downloadDocument(attachment);
          },
        });
    } else {
      // Fallback to local document service if no routing slip ID
      this.documentService.downloadDocument(attachment);
    }
  }

  onDocumentOpenInNewTab(attachment: DocumentAttachment): void {
    this.documentService.viewDocument(attachment);
  }

  getStatusClass(status?: string): string {
    if (!status) return 'status-pending';
    switch (status) {
      case 'Reviewed':
        return 'status-reviewed';
      case 'Approved':
        return 'status-approved';
      case 'Pending':
        return 'status-pending';
      case 'Rejected':
        return 'status-rejected';
      default:
        return 'status-none';
    }
  }

  /**
   * Load user details for a new comment
   */
  private loadUserDetailsForNewComment(newComment: Comment): void {
    this.usersService
      .getUserById(newComment.createdBy)
      .pipe(
        map((user) => ({
          id: newComment.createdBy,
          name: user.fullName,
          email: user.email,
        })),
        catchError(() =>
          of({ id: newComment.createdBy, name: 'Unknown User', email: '' })
        )
      )
      .subscribe((userDetails) => {
        // Cache the user details
        this.userDetailsCache.set(userDetails.id, {
          name: userDetails.name,
          email: userDetails.email,
        });

        // Add the new comment to the comments array
        const commentDisplay = {
          text: newComment.comment,
          by: userDetails.name,
          date: this.formatDate(newComment.createdOn),
          time: this.formatTime(newComment.createdOn),
        };
        this.comments.unshift(commentDisplay);

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Comment added successfully',
        });
      });
  }

  /**
   * Extract error message from HTTP error response
   * @param error HTTP error object
   * @returns Extracted error message or fallback message
   */
  private extractErrorMessage(error: any): string {
    const fallback = 'Something went wrong. Please try again.';
    const body = error?.error;
    // Backend may return a plain string (e.g. "User already worked on this document...")
    if (typeof body === 'string' && body.trim()) {
      return body.trim();
    }
    // ...or a JSON object with the message under a common key.
    if (body && typeof body === 'object') {
      const message = body.message ?? body.error ?? body.detail ?? body.title;
      if (typeof message === 'string' && message.trim()) {
        return message.trim();
      }
    }
    if (typeof error?.message === 'string' && error.message.trim()) {
      return error.message.trim();
    }
    return fallback;
  }
}
